import React, { forwardRef, useEffect, useImperativeHandle, useCallback, useState } from 'react';
import { Button, Dialog, DialogActions, DialogTitle, IconButton, CircularProgress, makeStyles, Icons, useTheme } from '~/lib/bn-material-ui';
import { usePrevious } from '~/utility/BinSentry-ui-utility';
import _ from 'lodash';
import classnames from 'classnames';
import { useTranslation } from '~/translations';

const useStyles = makeStyles(() => {
  return {
    dialog: {
      position: 'absolute',
    },
    header: {
      alignItems: 'center',
      display: 'flex',
      justifyContent: 'space-between',
    },
    disablePointerEvents: {
      '& > div': {
        pointerEvents: 'none',
      },
    },
    circularProgressBackground: {
      height: '100%',
      width: '100%',
      position: 'absolute',
      backgroundColor: '#00000051',
      left: 0,
      top: 0,
      zIndex: 10,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      transition: 'opacity 1s',
      pointerEvents: 'none',
    },
    closeButton: {
      marginLeft: 'auto',
      marginBottom: 'auto',
      padding: 0,
      height: '2.5rem',
      width: '2.5rem',
    },
    dialogActions: {
      paddingRight: '1rem',
      paddingBottom: '1rem',
      '& button': {
        marginLeft: '1.125rem !important',
      },
    },
  };
});

export const BnDialog = forwardRef(function BnDialog({
  overrideDialogActions,
  cancelHidden = false,
  restingWidth,
  deleteDisabled,
  submitDisabled,
  submitLabel, // defaults to translated bnCommonComponents.dialogSubmit
  title,
  open,
  onOpen,
  onDelete,
  onSubmit,
  onClose,
  children,
  submitButtonProps = {},
  closeOnSuccess = false,
  titleClassName,
  showProgressOverlay = false,
  ...defaultProps
}, ref) {
  const styles = useStyles();
  const { t } = useTranslation();
  const [submitting, setSubmitting] = useState(false);
  const previousOpen = usePrevious(open);

  useEffect(() => {
    if (!open || (previousOpen === open)) {
      return;
    }

    if (onOpen) {
      onOpen();
    }
  }, [previousOpen, open, onOpen]);

  const handleSubmit = async () => {
    setSubmitting(true);
    try {
      const success = await onSubmit();
      if (success && closeOnSuccess) {
        onClose();
      }
    } finally {
      setSubmitting(false);
    }
  };

  useImperativeHandle(ref, () => ({
    submit() {
      handleSubmit();
    },
  }));

  const dialogClasses = classnames(
    defaultProps.className,
    styles.dialog,
    (showProgressOverlay || submitting) && styles.disablePointerEvents,
  );

  const noBackdropClose = useCallback((_event, reason) => {
    if (reason === 'backdropClick') {
      return;
    }

    onClose();
  }, [onClose]);

  const restingWidthStyles = useRestingWidthDynamicStyles(restingWidth);

  return (
    <Dialog
      open={!!open}
      onClose={noBackdropClose}
      {...defaultProps}
      {...restingWidthStyles ?
        { PaperProps: { style: restingWidthStyles } } :
        {}
      }
      className={dialogClasses}
    >
      <div className={styles.circularProgressBackground} style={{ opacity: showProgressOverlay || submitting ? 1 : 0 }}>
        <CircularProgress/>
      </div>
      <DialogTitle className={classnames(styles.header, titleClassName)}>
        {title}
        <IconButton
          className={styles.closeButton}
          onClick={(e) => {e.stopPropagation(); onClose();}}
          size='large'>
          <Icons.Close />
        </IconButton>
      </DialogTitle>
      {children}
      {!overrideDialogActions &&
        <DialogActions className={styles.dialogActions}>
          {!cancelHidden && onClose &&
            <Button variant='outlined' onClick={onClose}>
              {t('bnCommonComponents.dialogCancel')}
            </Button>
          }
          {onDelete &&
            <Button variant='contained' color='error' disabled={deleteDisabled} onClick={onDelete}>
              {t('bnCommonComponents.dialogDelete')}
            </Button>
          }
          {onSubmit &&
            <Button variant='contained' disabled={submitDisabled || submitting} onClick={handleSubmit} {...submitButtonProps}>
              {submitLabel || t('bnCommonComponents.dialogSubmit')}
            </Button>
          }
        </DialogActions>
      }
    </Dialog>
  );
});

// These mirror the hardcoded values found in MUIs Dialog.js
const DIALOG_WINDOW_MARGIN = '64px';
const DEFAULT_XS_WIDTH_PX = 444;

function useRestingWidthDynamicStyles (restingWidth) {
  const theme = useTheme();
  const restingWidthRawValue = theme.breakpoints.values[restingWidth];
  if (restingWidthRawValue === undefined) {
    return undefined;
  }

  if (theme.breakpoints.unit !== 'px') {
    throw new Error(`Unsupported theme.breakpoints.unit value: ${theme.breakpoints.unit}. Only px is supported`);
  }

  // theme.breakpoints.values.xs width is often 0. This is mirroring logic for maxWidth in MUIs Dialog.js
  const restingWidthValue = restingWidth === 'xs' ? Math.max(restingWidthRawValue, DEFAULT_XS_WIDTH_PX) : restingWidthRawValue;
  return {
    minWidth: `calc(min(100% - ${DIALOG_WINDOW_MARGIN}, ${restingWidthValue}px))`,
  };
}

export const BnDialogButton = forwardRef(function BnDialogButton({ buttonText, icon, buttonVariant, buttonProps = {}, children, ...props }, ref) {
  const [open, setOpen] = useState(false);

  const handleClose = () => {
    setOpen(false);
    props.onClose?.();
  };
  const handleOpen = () => setOpen(true);

  return <>
    <Button startIcon={icon} variant={buttonVariant ?? 'text'} onClick={handleOpen} {...buttonProps}>
      {buttonText}
    </Button>
    <BnDialog ref={ref} open={open} onClose={handleClose} {..._.omit(props, 'onClose')}>
      {children}
    </BnDialog>
  </>;
});
